import React, { useState, useEffect, useRef } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const TABS = [{ id: "grocery", label: "Grocery" }, { id: "all", label: "All" }, { id: "wishlist", label: "Wishlist" }];
const NOTION_URL = "https://app.notion.com/p/8ec6c824fdd949059918044348c35ce4";

async function apiCreate(value, attempt = 1) {
  const res = await fetch("/.netlify/functions/notion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "createShoppingItem", value }) });
  if (res.status === 429 && attempt < 3) {
    await new Promise(r => setTimeout(r, 600 * attempt));
    return apiCreate(value, attempt + 1);
  }
  const data = await res.json();
  if (!res.ok) { console.error("createShoppingItem failed:", data.error); throw new Error("Failed to add item"); }
  return data.item;
}
async function apiUpdate(pageId, value) {
  const res = await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId, action: "updateShoppingItem", value }) });
  if (!res.ok) throw new Error("Failed to update item");
}
async function apiDelete(pageId) {
  const res = await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId, action: "delete" }) });
  if (!res.ok) throw new Error("Failed to delete item");
}

function ItemRow({ item, canModerate, editingId, setEditingId, onToggleBought, onSave, onDelete, stores }) {
  const [name, setName] = useState(item.name);
  const [store, setStore] = useState(item.store || stores[0]);
  const editing = editingId === item.id;

  useEffect(() => { setName(item.name); setStore(item.store || stores[0]); }, [item.name, item.store, editing, stores]);

  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <div onClick={() => setEditingId(editing ? null : item.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", cursor: "pointer" }}>
        <button
          aria-label="Mark bought"
          onClick={(e) => { e.stopPropagation(); if (canModerate) onToggleBought(item); }}
          style={{ boxSizing: "border-box", appearance: "none", WebkitAppearance: "none", margin: 0, padding: 0, width: 22, height: 22, minWidth: 22, borderRadius: "50%", flexShrink: 0, border: `2px solid ${item.bought ? "var(--sage)" : "var(--border)"}`, background: item.bought ? "var(--sage)" : "var(--white)", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0, opacity: canModerate ? 1 : 0.45, cursor: canModerate ? "pointer" : "default" }}
        >
          {item.bought && <span style={{ color: "white", fontSize: 12, fontWeight: 800, lineHeight: 1 }}>✓</span>}
        </button>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: item.bought ? "var(--muted)" : "var(--charcoal)", textDecoration: item.bought ? "line-through" : "none" }}>{item.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {item.store && <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1, padding: "3px 7px", borderRadius: 20, background: "var(--cream-hover)", color: "var(--mid)" }}>{item.store}</span>}
            {item.type === "Garden" && <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1, padding: "3px 7px", borderRadius: 20, background: "var(--sage-muted)", color: "var(--sage)" }}>Garden</span>}
          </div>
        </div>
        {canModerate && <div style={{ width: 16, flexShrink: 0 }} />}
      </div>

      {editing && (
        <div style={{ padding: "0 0 12px 32px", display: "flex", flexDirection: "column", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ fontSize: 14, height: 52, padding: "0 14px", boxSizing: "border-box", width: "100%" }} />
          <select value={store} onChange={(e) => setStore(e.target.value)} style={{ fontSize: 14, height: 52, padding: "0 14px", boxSizing: "border-box", width: "100%" }}>
            {stores.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onSave(item, { name: name.trim() || item.name, store })} style={{ flex: 1, padding: "7px 0", fontSize: 12, fontWeight: 700, color: "var(--sage)", border: "1px solid var(--sage)", background: "var(--sage-muted)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
            {canModerate && <button onClick={() => onDelete(item)} style={{ padding: "7px 12px", fontSize: 12, fontWeight: 700, color: "var(--rose)", border: "1px solid var(--border)", background: "var(--white)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function StoreFilterDropdown({ selected, setSelected, stores }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allChecked = selected.size === stores.length;
  const noneChecked = selected.size === 0;
  const label = allChecked ? "All stores" : noneChecked ? "None selected" : `${selected.size} selected`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: open ? "var(--sage)" : "var(--cream-hover)", border: "1.5px solid", borderColor: open ? "var(--sage)" : "var(--border)", borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: open ? "white" : "var(--charcoal)", fontFamily: "inherit", cursor: "pointer" }}>
        <span>{label}</span>
        <span style={{ fontSize: 10, marginLeft: 4 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
          <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
            <button onClick={() => setSelected(new Set(stores))} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", borderRight: "1px solid var(--border)", fontSize: 12, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Select all</button>
            <button onClick={() => setSelected(new Set())} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", fontSize: 12, fontWeight: 800, color: "var(--rose)", cursor: "pointer", fontFamily: "inherit" }}>Deselect all</button>
          </div>
          {stores.map(s => {
            const checked = selected.has(s);
            return (
              <button key={s} onClick={() => { const next = new Set(selected); if (checked) next.delete(s); else next.add(s); setSelected(next); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: checked ? "var(--sage-muted)" : "none", border: "none", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--charcoal)", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box", background: checked ? "var(--sage)" : "transparent", border: `2px solid ${checked ? "var(--sage)" : "var(--border)"}` }}>
                  {checked && <span style={{ color: "white", fontSize: 12, fontWeight: 800 }}>✓</span>}
                </span>
                {s}
              </button>
            );
          })}
          <button onClick={() => setOpen(false)} style={{ width: "100%", padding: "11px 14px", background: "var(--sage-muted)", border: "none", fontSize: 13, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Done</button>
        </div>
      )}
    </div>
  );
}

// Note: unlike other pages' `readOnly` prop (which hides adding entirely),
// kids CAN add and edit shopping items here — they just can't check items
// off or delete them, so an accidental tap can't wipe out the list.
export default function ShoppingPage({ onBack, onTabTap, isKidUser = false }) {
  const canModerate = !isKidUser;
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState(["Grocery"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("grocery");
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [boughtOpen, setBoughtOpen] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [addError, setAddError] = useState(null);

  useEffect(() => {
    fetch("/.netlify/functions/notion?type=shopping")
      .then(r => r.json())
      .then(d => { setItems(d.items || []); setLoading(false); })
      .catch(() => { setError("Couldn't load the shopping list."); setLoading(false); });
    fetch("/.netlify/functions/notion?type=shoppingStores")
      .then(r => r.json())
      .then(d => { const opts = d.options && d.options.length ? d.options : ["Grocery"]; setStores(opts); setSelectedStores(new Set(opts.filter(s => s !== "Grocery"))); })
      .catch(() => {});
  }, []);

  const changeTab = (id) => { setTab(id); setEditingId(null); setBoughtOpen(false); };

  let scoped = items;
  if (tab === "grocery") scoped = items.filter(i => i.store === "Grocery" && !i.wishlist);
  if (tab === "all") scoped = items.filter(i => !i.wishlist && selectedStores.has(i.store));
  if (tab === "wishlist") scoped = items.filter(i => i.wishlist);

  const active = scoped.filter(i => !i.bought);
  const bought = tab === "grocery" ? scoped.filter(i => i.bought) : [];

  const handleToggleBought = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, bought: !i.bought } : i));
    try { await apiUpdate(item.id, { bought: !item.bought }); } catch { setError("Couldn't save that change."); }
  };

  const handleSave = async (item, patch) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...patch } : i));
    setEditingId(null);
    try { await apiUpdate(item.id, patch); } catch { setError("Couldn't save that change."); }
  };

  const handleDelete = async (item) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    setEditingId(null);
    try { await apiDelete(item.id); } catch { setError("Couldn't remove that item."); }
  };

  const handleClearBought = async () => {
    const toClear = bought;
    setItems(prev => prev.filter(i => !toClear.some(b => b.id === i.id)));
    setBoughtOpen(false);
    try { await Promise.all(toClear.map(i => apiDelete(i.id))); } catch { setError("Couldn't clear everything bought."); }
  };

  const handleAdd = async () => {
    const val = newItemName.trim();
    if (!val) { setAddError("Enter an item name first"); return; }
    setAddError(null);
    const store = tab === "grocery" ? "Grocery" : (stores.find(s => s !== "Grocery") || stores[0] || "Grocery");
    const wishlist = tab === "wishlist";
    try {
      const item = await apiCreate({ name: val, store, wishlist });
      setItems(prev => [...prev, item]);
      setNewItemName("");
    } catch { setAddError("Couldn't add that item — try again."); }
  };

  return (
    <div className="app">
      <div style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>Shopping</span>
      </div>

      <div className="scroll-body">
        <div style={{ padding: "12px 20px 4px" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Add an item" style={{ flex: 1, fontSize: 14, height: 52, padding: "0 14px", boxSizing: "border-box" }} onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }} />
            <button onClick={handleAdd} style={{ padding: "0 18px", height: 52, boxSizing: "border-box", fontSize: 14, color: "var(--sage)", fontWeight: 700, background: "none", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
          </div>
          {addError && <div style={{ fontSize: 11, color: "var(--rose)", marginTop: 4 }}>{addError}</div>}
        </div>

        <div style={{ display: "flex", gap: 6, padding: "12px 20px 0" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => changeTab(t.id)} style={{ flex: 1, padding: "7px 0", fontSize: 12, fontWeight: 700, borderRadius: 20, border: `1px solid ${tab === t.id ? "var(--sage)" : "var(--border)"}`, background: tab === t.id ? "var(--sage-muted)" : "var(--white)", color: tab === t.id ? "var(--sage)" : "var(--mid)", cursor: "pointer", fontFamily: "inherit" }}>{t.label}</button>
          ))}
        </div>

        {tab === "all" && (
          <div style={{ position: "relative", padding: "10px 20px 0" }}>
            <StoreFilterDropdown selected={selectedStores} setSelected={setSelectedStores} stores={stores} />
          </div>
        )}

        <div style={{ padding: "10px 20px 4px" }}>
          {loading ? (
            <div style={{ padding: "20px 0", textAlign: "center", fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Loading…</div>
          ) : !active.length ? (
            <div style={{ padding: "20px 0", textAlign: "center", fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{tab === "grocery" && bought.length ? "All caught up." : "Nothing here yet."}</div>
          ) : (
            active.map(item => (
              <ItemRow key={item.id} item={item} canModerate={canModerate} editingId={editingId} setEditingId={setEditingId} onToggleBought={handleToggleBought} onSave={handleSave} onDelete={handleDelete} stores={stores} />
            ))
          )}
        </div>

        {tab === "grocery" && bought.length > 0 && (
          <div style={{ padding: "0 20px 4px" }}>
            <div onClick={() => setBoughtOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", cursor: "pointer" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", transform: boughtOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span> Bought ({bought.length})
              </div>
              {canModerate && <button onClick={(e) => { e.stopPropagation(); handleClearBought(); }} style={{ fontSize: 11, fontWeight: 700, color: "var(--rose)", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>Clear bought</button>}
            </div>
            {boughtOpen && bought.map(item => (
              <ItemRow key={item.id} item={item} canModerate={canModerate} editingId={editingId} setEditingId={setEditingId} onToggleBought={handleToggleBought} onSave={handleSave} onDelete={handleDelete} stores={stores} />
            ))}
          </div>
        )}

        {tab !== "grocery" && (
          <div style={{ padding: "0 20px 14px" }}>
            <a href={NOTION_URL} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--sage)", textDecoration: "none" }}>View full list in Notion ↗</a>
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}
      </div>

      <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
          </div>
        ))}
      </nav>
    </div>
  );
}
